import React, { useState, useEffect, useRef } from 'react';
import ActiveFilterChip from './ActiveFilterChip';

export interface ActiveFilter {
  id: string;
  label: string;
  value: string;
}

export interface FilterBarProps {
  children: React.ReactNode;
  activeFilters?: ActiveFilter[];
  onClearAll?: () => void;
}

/**
 * FilterBar — container that renders children in a sticky horizontal row.
 * On < 1024px (lg breakpoint), collapses to a bottom sheet with a "Filters (n)" button.
 * Renders ActiveFilterChipRow below itself if activeFilters prop is non-empty.
 */
export default function FilterBar({
  children,
  activeFilters = [],
  onClearAll,
}: FilterBarProps) {
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const desktopBarRef = useRef<HTMLDivElement>(null);

  const handleRemoveFilter = (id: string) => {
    // This will be handled by the consumer via onClearAll or a more granular callback
    // For now, we rely on the parent to handle filter updates
  };

  // Publish --filter-bar-h CSS custom property so DataTable thead can stack
  // below it via top: calc(var(--app-header-h) + var(--filter-bar-h, 0px)).
  // Note: --subregion-controls-h is published by MapView for views that have
  // their own controls strip; DataTable does not currently consume it.
  useEffect(() => {
    const el = desktopBarRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--filter-bar-h', `${h}px`);
    };

    measure();

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      // Clear on unmount so views without a FilterBar don't inherit a stale value.
      document.documentElement.style.setProperty('--filter-bar-h', '0px');
    };
    // ResizeObserver should catch height changes, but in practice chip-row
    // appearance can race the observer's first callback. Re-running the
    // effect when activeFilters.length changes guarantees a fresh measure
    // immediately after the layout settles.
  }, [activeFilters.length]);

  return (
    <>
      {/* Desktop filter bar (visible on lg and up) */}
      <div
        ref={desktopBarRef}
        className="hidden lg:block sticky top-[var(--app-header-h)] z-30 bg-bg-elevated border-b border-border-base shadow-ds-sm"
      >
        <div className="p-4 space-y-4">
          {/* Filter controls row */}
          <div className="flex flex-wrap gap-3 items-end">{children}</div>

          {/* Active filter chips row */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {activeFilters.map((filter) => (
                <ActiveFilterChip
                  key={filter.id}
                  label={filter.label}
                  value={filter.value}
                  onRemove={() => handleRemoveFilter(filter.id)}
                />
              ))}
              {onClearAll && (
                <button
                  onClick={onClearAll}
                  className="text-sm text-fg-secondary hover:text-fg-primary underline focus:outline-none focus:ring-2 focus:ring-accent rounded px-2 py-1"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter bar (visible on < lg) */}
      <div className="lg:hidden sticky top-[var(--app-header-h)] z-30 bg-bg-elevated border-b border-border-base shadow-ds-sm p-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFiltersSheet(!showFiltersSheet)}
            className="flex items-center gap-2 px-3 py-2 min-h-11 rounded bg-bg-raised hover:bg-bg-elevated text-fg-primary text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label={`Filters (${activeFilters.length})`}
          >
            <span>Filters</span>
            {activeFilters.length > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-white text-xs font-bold">
                {activeFilters.length}
              </span>
            )}
          </button>
        </div>

        {/* Bottom sheet content when open */}
        {showFiltersSheet && (
          <div className="mt-4 p-4 bg-bg-base rounded border border-border-base space-y-4">
            {children}

            {/* Active chips in sheet */}
            {activeFilters.length > 0 && (
              <div className="border-t border-border-base pt-4 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {activeFilters.map((filter) => (
                    <ActiveFilterChip
                      key={filter.id}
                      label={filter.label}
                      value={filter.value}
                      onRemove={() => handleRemoveFilter(filter.id)}
                    />
                  ))}
                </div>
                {onClearAll && (
                  <button
                    onClick={onClearAll}
                    className="w-full px-3 py-2 rounded bg-bg-raised hover:bg-bg-elevated text-fg-primary text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}

            <button
              onClick={() => setShowFiltersSheet(false)}
              className="w-full px-3 py-2 rounded bg-accent hover:opacity-90 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Note: a separate desktop-only sticky chip row used to live here with a
          magic top-[124px] offset. It was redundant with the chips already
          rendered inside the desktop bar above (and the magic number broke
          when AppShell rows changed). Removed. */}
    </>
  );
}
